import { type FC, useCallback, useState } from 'react'

import api from 'STORE/api'
import { closeCreditsTopUp, selectIsCreditsTopUpOpen } from 'STORE/creditsTopUp/creditsTopUpSlice'
import { useCreditBalanceQuery, useCreditPackagesQuery } from 'STORE/endpoints/credits'
import { useAppDispatch, useAppSelector } from 'STORE/index'

import PaymentModal from '../PaymentPlansContainer/components/PaymentModal'
import CreditPackagesPicker from './CreditPackagesPicker'
import CreditsCheckout from './CreditsCheckout'
import { useBalanceAfterPurchase } from './useBalanceAfterPurchase'

const CreditsTopUpModal: FC = () => {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectIsCreditsTopUpOpen)

  const { data: balance, refetch: refetchBalance } = useCreditBalanceQuery()
  const { data: packages = [], isLoading: isPackagesLoading } = useCreditPackagesQuery(undefined, {
    skip: !isOpen,
  })

  const [checkoutPackageId, setCheckoutPackageId] = useState<string | null>(null)

  const { markPurchaseStarted, cancelPurchase } = useBalanceAfterPurchase({
    balance: balance?.totalCredits,
    isCheckoutOpen: checkoutPackageId !== null,
    refetchBalance,
  })

  const closeModal = useCallback((): void => {
    dispatch(closeCreditsTopUp())
    setCheckoutPackageId(null)
  }, [dispatch])

  const handleClose = (): void => {
    if (checkoutPackageId !== null) cancelPurchase()
    closeModal()
  }

  const handlePaymentComplete = (): void => {
    dispatch(api.util.invalidateTags(['Credits']))
    closeModal()
  }

  const handleConfirm = (packageId: string): void => {
    markPurchaseStarted()
    setCheckoutPackageId(packageId)
  }

  return (
    <PaymentModal open={isOpen} onClose={handleClose}>
      {checkoutPackageId === null ? (
        <CreditPackagesPicker
          packages={packages}
          isLoading={isPackagesLoading}
          onConfirm={handleConfirm}
          onCancel={handleClose}
        />
      ) : (
        <CreditsCheckout
          key={checkoutPackageId}
          packageId={checkoutPackageId}
          onComplete={handlePaymentComplete}
        />
      )}
    </PaymentModal>
  )
}

export default CreditsTopUpModal
